import { FunctionComponent, useCallback, useRef, useState } from 'react'

import * as Yup from 'yup'
import { FormikProps, FormikValues } from 'formik'
import { useFrotista } from 'modules/user/hooks/useFrotista'
import SnackBar from 'shared/components/SnackBar'
import { colors } from 'shared/styles/colors'
import { encrypt } from 'shared/utils/crypt'
import { registerEvent } from 'shared/services/TrackingGAService'

import IGerarBoletoDepositoRequest from 'modules/pagamentos/domain/entities/IGerarBoletoDepositoRequest'
import { ContainerNovoBoleto, ErrorMessage, FormWrapper, Title } from './styles'
import Form from 'shared/components/Form'
import Alert from 'shared/components/Alert'
import { FaInfo } from 'react-icons/fa'
import Button from 'shared/components/Button'
import { IBoletoDepositoResponse } from 'modules/pagamentos/domain/entities/IBoletoDepositoResponse'
import { IGerarBoletoDeposito } from 'modules/pagamentos/domain/usecases/GerarBoletoDeposito'
import { IBaixarPdfBoletoDeposito } from 'modules/pagamentos/domain/usecases/BaixarPdfBoletoDeposito'
import ModalSucesso from './components/ModalSucesso'
import ModalErro from './components/ModalErro'
import { NumericFormat } from 'react-number-format'
import Input from 'shared/components/Input'

type TProps = {
  gerarBoletoDeposito: IGerarBoletoDeposito
  baixarPdfBoletoDeposito: IBaixarPdfBoletoDeposito
}

const NovoBoleto: FunctionComponent<TProps> = ({
  gerarBoletoDeposito,
  baixarPdfBoletoDeposito,
}) => {
  const { frotistaId } = useFrotista()
  const formRef = useRef<FormikProps<FormikValues>>()
  const [loading, setLoading] = useState(false)
  const [openErrorMessage, setOpenErrorMessage] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [validationMessage, setValidationMessage] = useState<string>(null)
  const [openModalSucesso, setOpenModalSucesso] = useState(false)
  const [openModalErro, setOpenModalErro] = useState(false)
  const [boletoGerado, setBoletoGerado] =
    useState<IBoletoDepositoResponse>(null)
  const closeModalSucesso = () => {
    setOpenModalSucesso(!openModalSucesso)
  }

  const closeModalErro = () => {
    setOpenModalErro(!openModalErro)
  }

  const handleSubmit = useCallback(
    async (value: any) => {
      setBoletoGerado(null)
      registerEvent({
        categoria: 'Pagamentos',
        acao: 'Novo boleto depósito',
        local: 'Tela de trazer dinheiro',
      })
      setLoading(true)

      try {
        const schema = Yup.object().shape({
          valor: Yup.number()
            .required()
            .min(20, 'O valor minímo é de R$ 20,00')
            .max(5000, 'O valor máximo é de R$ 5.000,00'),
        })

        await schema.validate(
          {
            valor: value.valor,
          },
          {
            abortEarly: false,
          },
        )
        const dados = {
          valor: +value.valor?.toString(),
        }
        const request: IGerarBoletoDepositoRequest = {
          body: encrypt(dados, false, frotistaId.toString()),
          frotista_id: frotistaId,
        }
        const response = await gerarBoletoDeposito.execute(request)
        setBoletoGerado(response)
        setLoading(false)
        setOpenModalSucesso(true)
      } catch (e: unknown) {
        setOpenModalErro(true)
        if (e instanceof Error) {
          setErrorMessage(e.message || 'Erro ao atualizar os valores')
        } else {
          setErrorMessage('Erro ao atualizar os valores')
        }
        setOpenErrorMessage(true)
        setLoading(false)
      }
    },
    [frotistaId],
  )

  const handleBaixarPdfBoletoDeposito = async () => {
    setLoading(true)

    try {
      const response = await baixarPdfBoletoDeposito.execute({
        frotista_id: frotistaId,
        codigo_de_barras: boletoGerado.codigo_de_barras,
        id_unico_dock: boletoGerado.id_unico_dock,
      })
      const file = new Blob([response], { type: 'application/pdf' })
      const fileURL = URL.createObjectURL(file)
      window.open(fileURL)
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message || 'Erro ao atualizar os valores')
      } else {
        setErrorMessage('Erro ao atualizar os valores')
      }
      setOpenErrorMessage(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <ContainerNovoBoleto>
        <Title>Digite o valor do boleto</Title>
        <Form
          onSubmit={handleSubmit}
          initialValues={{
            valor: '',
          }}
          validationSchema={() => ({
            valor: Yup.number()
              .required()
              .min(20, 'O valor minímo é de R$ 20,00')
              .max(5000, 'O valor máximo é de R$ 5.000,00'),
          })}
          innerRef={formRef}
        >
          {formControl => (
            <FormWrapper>
              <NumericFormat
                name="valor"
                customInput={Input}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale={true}
                prefix="R$ "
                allowLeadingZeros={true}
                onValueChange={values => {
                  formControl.setFieldValue('valor', values.floatValue)

                  if (values.floatValue < 20) {
                    setValidationMessage('O valor minímo é de R$ 20,00')
                  } else if (values.floatValue > 5000) {
                    setValidationMessage('O valor máximo é de R$ 5.000,00')
                  } else if (
                    values.floatValue >= 20 &&
                    values.floatValue <= 5000
                  ) {
                    setValidationMessage('')
                  }
                }}
              />
              <ErrorMessage>{validationMessage}</ErrorMessage>
              <Alert
                icon={FaInfo}
                type="info"
                message="A emissão de cada boleto gera uma taxa de R$ 1,80 que será debitada da sua conta."
              />
              <Button
                color={colors.green}
                type="submit"
                onClick={formControl.handleSubmit}
                disabled={!!validationMessage || loading}
                loading={loading}
                style={{ width: '100%' }}
              >
                Gerar boleto
              </Button>
            </FormWrapper>
          )}
        </Form>
      </ContainerNovoBoleto>
      <SnackBar
        open={openErrorMessage}
        setOpen={setOpenErrorMessage}
        severity="error"
        message={errorMessage}
        horizontal="right"
      />
      <ModalSucesso
        openModal={openModalSucesso}
        closeModal={closeModalSucesso}
        loading={loading}
        handleBaixarPdfBoletoDeposito={handleBaixarPdfBoletoDeposito}
      />
      <ModalErro openModal={openModalErro} closeModal={closeModalErro} />
    </>
  )
}

export default NovoBoleto
